// LLM prompt builders + zod validators for the 3 call types:
// spawn (NPC creator), dialogue (scripted exchange), delta (relationship JSON).
// Content policy: edgy-but-safe — raw, uncomfortable topics and arguments
// allowed; no explicit sexual/violent content; respect provider filters.
import { z } from 'zod';
import { TRAIT_KEYS, TRAIT_SEEDS } from '../db/traits.js';
import type { LengthClass, Npc, Relationship, Memory, SleepState } from '../types.js';
import { SCENARIOS, type Scenario } from '../sim/scenarios.js';
import { TUNING } from '../constants.js';
import type { WorldLaunchHandoff } from '../ui/CreateWorld/draftStorage.js';
import { formatWorldClockPrompt } from '../ui/worldRuntime/clock.js';

// --- Validators (deterministic parse into DB) ---

// LLMs often wrap JSON in ```json fences or add prose around it, even in
// json_object mode. Recover the outer object instead of failing the call.
export function parseJsonLoose(text: string): unknown {
  const t = text.trim().replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/, '');
  const start = t.indexOf('{');
  const end = t.lastIndexOf('}');
  if (start === -1 || end <= start) throw new Error('No JSON object in LLM response');
  return JSON.parse(t.slice(start, end + 1));
}
const NatureSchema = z.object({
  sociability: z.number().int().min(1).max(10),
  agreeableness: z.number().int().min(1).max(10),
  emotionality: z.number().int().min(1).max(10),
  caution: z.number().int().min(1).max(10),
  discipline: z.number().int().min(1).max(10),
  dominance: z.number().int().min(1).max(10),
  empathy: z.number().int().min(1).max(10),
  curiosity: z.number().int().min(1).max(10),
  drive: z.number().int().min(1).max(10),
  sexual_drive: z.number().int().min(1).max(10),
  casual_sex_openness: z.number().int().min(1).max(10),
});

const NurtureSchema = z
  .array(
    z.object({
      trait_key: z.string().refine((k) => TRAIT_KEYS.has(k), 'unknown trait_key'),
      strength: z.number().int().min(1).max(10),
      reason: z.string().min(8).max(200),
    }),
  )
  .min(3)
  .max(8);

export const SpawnSchema = z.object({
  first_name: z.string().min(1).max(40),
  last_name: z.string().min(1).max(40),
  palette: z.object({
    skin: z.string(),
    shirt: z.string(),
    pants: z.string(),
    hair: z.string(),
  }),
  nature: NatureSchema,
  nurture: NurtureSchema,
  state: z.object({
    mood: z.string(),
    stress: z.number().int().min(1).max(10),
    energy: z.number().int().min(1).max(10),
  }),
});

// Delta response: each field -2..+2 (0 low impact, ±1 moderate, ±2 strong).
// familiarity only grows (0..+2).
const DeltaSide = z.object({
  familiarity: z.number().int().min(0).max(2),
  affinity: z.number().int().min(-2).max(2),
  trust: z.number().int().min(-2).max(2),
  respect: z.number().int().min(-2).max(2),
  fear: z.number().int().min(-2).max(2),
  attraction: z.number().int().min(-2).max(2),
});

export const DeltaSchema = z.object({
  a_to_b: DeltaSide,
  b_to_a: DeltaSide,
  memories: z
    .array(
      z.object({
        from: z.enum(['a', 'b']),
        summary: z.string().min(4).max(160),
        salience: z.number().int().min(1).max(10),
      }),
    )
    .min(1)
    .max(4),
});

// --- Prompt builders ---
// Full approved nurture vocabulary, grouped for the spawn prompt.
// The model MUST pick trait_key from exactly these keys — without the list
// it invents keys, zod rejects, and every NPC degrades to the fallback.
export function nurtureVocabBlock(): string {
  const byCat = new Map<string, string[]>();
  for (const [key, cat] of TRAIT_SEEDS) {
    const list = byCat.get(cat) ?? [];
    list.push(key);
    byCat.set(cat, list);
  }
  return [...byCat.entries()].map(([cat, keys]) => `${cat}: ${keys.join(', ')}`).join('\n');
}

export function buildSpawnPrompt(neurotic = false, takenNames: string[] = []): string {
  const avoid = takenNames.length > 0 ? `\nAlready used in this world — do NOT reuse: ${takenNames.join(', ')}.` : '';
  return `Create one random NPC for a social simulation game. Return ONLY JSON matching this shape:
{"first_name": string, "last_name": string,
 "palette": {"skin": hex, "shirt": hex, "pants": hex, "hair": hex},
 "nature": {sociability, agreeableness, emotionality, caution, discipline, dominance, empathy, curiosity, drive, sexual_drive, casual_sex_openness — each int 1-10, mostly 3-8, avoid excess 1s/10s},
 "nurture": [3-8 items of {"trait_key": EXACTLY one key from the vocabulary below, "strength": 1-10, "reason": exactly one concise causal sentence describing WHAT HAPPENED}],
 "state": {"mood": one of neutral,happy,excited,content,sad,angry,afraid,anxious,ashamed,guilty,jealous,lonely,frustrated,hopeful,affectionate, "stress": 1-10, "energy": 1-10}}
NAMES: invent a distinctive, uncommon full name — never a generic default. Avoid overused AI names like Alex, Sam, Jordan, Taylor, Emma, Liam, Sarah, John, Mia, Ava, Ethan, Daniel, Marcus, Kai (as first name), or plain surnames like Smith, Johnson, Lee. Reach for varied cultures, eras, and sounds (e.g. Solveig, Tadeo, Isolde, Kenji, Odette, Bram, Zadie, Cosmo, Ines, Rufus). First and last name must differ from each other.${avoid}
APPROVED NURTURE VOCABULARY (trait_key must match exactly, no inventions):
${nurtureVocabBlock()}
Rules: Nurture may reinforce or contradict Nature. Give a full name with varied backgrounds. Palette: pleasant Stardew-like colors.${neurotic ? NEUROTIC_BLOCK : ''}`;
}

// 50/50 wild card: bias the model toward poles and intensity — probability,
// not certainty. Not every value must be extreme, but this is no moderate.
const NEUROTIC_BLOCK = `

WILD CARD — NEUROTIC TEMPERAMENT: wire this NPC hot. Bias nature scores toward the poles (1s and 10s) instead of the safe middle — extreme, polarizing, a lot. Let nurture strengths run high and make the reasons hit harder: profound joy, deep love, real trauma, sharp pain. Bias, not caricature: leave a few values moderate so the extremes mean something.`;


export function dialogueTargetCount(cls: LengthClass): [number, number] {
  const [lo, hi] = TUNING.dialogueLengths[cls];
  return [lo, hi];
}

function profileBlock(npc: Npc): string {
  const nat = Object.entries(npc.nature)
    .map(([k, v]) => `${k}=${v}`)
    .join(', ');
  const nur = npc.nurture.map((t) => `- ${t.trait_key} (${t.strength}/10): ${t.reason}`).join('\n');
  return `${npc.firstName} ${npc.lastName}: Nature[${nat}]\nLearned traits:\n${nur}\nMood: ${npc.state.mood}, stress ${npc.state.stress}/10, energy ${npc.state.energy}/10`;
}

function relBlock(rel: Relationship | null, mems: Memory[], longterm: string, self: string, other: string): string {
  if (!rel) return `${self} barely knows ${other} (stranger).`;
  const m = mems
    .slice(0, TUNING.recentMemoryKeep)
    .map((x) => `[${x.salience}] ${x.summary}`)
    .join('\n');
  const bondLead =
    rel.bondKind && rel.bondKind !== 'stranger'
      ? `Toward ${other}: ${bondKindLabel(rel.bondKind)} (closeness ${rel.familiarity}/10).\n`
      : `Toward ${other}: familiarity ${rel.familiarity}/10, `;
  const scores =
    rel.bondKind && rel.bondKind !== 'stranger'
      ? `affinity ${rel.affinity}, trust ${rel.trust}, respect ${rel.respect}, fear ${rel.fear}/10, attraction ${rel.attraction}/10.`
      : `affinity ${rel.affinity}, trust ${rel.trust}, respect ${rel.respect}, fear ${rel.fear}/10, attraction ${rel.attraction}/10.`;
  return `${bondLead}${scores}\nMemories:\n${m || '(none yet)'}\nLong-term impression: ${longterm || '(none yet)'}`;
}

const BOND_KIND_LABELS: Record<string, string> = {
  sister: 'Sister', brother: 'Brother', friend: 'Friend', stranger: 'Stranger',
};

function bondKindLabel(kind: string): string {
  return BOND_KIND_LABELS[kind] ?? kind.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface CharacterDraftMetaWithNpcId {
  npcId?: string;
}

function answerValue(value: unknown): string {
  return typeof value === 'string' ? value : JSON.stringify(value);
}

function speakerWorldBlock(npc: Npc, handoff: WorldLaunchHandoff): string {
  const meta = handoff.characters.find((candidate) => {
    const npcId = (candidate as CharacterDraftMetaWithNpcId).npcId;
    return (npcId && npcId === npc.id) || candidate.firstName === npc.firstName;
  });
  const draft = meta
    ? handoff.snapshot.characters.find((candidate) => candidate.draftCharacterId === meta.draftCharacterId)
    : handoff.snapshot.characters.find((candidate) => candidate.basic.firstName === npc.firstName);
  const backgroundTrimmed = (draft?.background ?? '').trim();
  const lines = [
    `Speaker: ${npc.firstName} ${npc.lastName}`,
    (meta?.sex ?? draft?.sex) ? `Sex: ${meta?.sex ?? draft?.sex}` : '',
    (meta?.species ?? draft?.species) ? `Species: ${meta?.species ?? draft?.species}` : '',
    backgroundTrimmed ? `Background: ${backgroundTrimmed}` : '',
    (meta?.ageRange ?? draft?.ageRange) ? `Age range: ${meta?.ageRange ?? draft?.ageRange}` : '',
  ].filter(Boolean);
  const appearance = draft?.appearance;
  if (appearance) {
    lines.push(
      `Appearance/clothes: top ${appearance.topStyle} (${appearance.topColor}), `
      + `bottom ${appearance.bottomStyle} (${appearance.bottomColor}), `
      + `hair ${appearance.hairStyle} (${appearance.hairColor}), skin ${appearance.skinColor}`,
    );
  } else {
    lines.push(
      `Appearance/clothes colors: shirt ${npc.palette.shirt}, pants ${npc.palette.pants}, `
      + `hair ${npc.palette.hair}, skin ${npc.palette.skin}`,
    );
  }
  return lines.join('\n');
}

function worldBlock(handoff: WorldLaunchHandoff, a: Npc, b: Npc, worldNowIso?: string): string {
  const { snapshot } = handoff;
  const schedule = snapshot.schedule;
  const clockIso = worldNowIso ?? snapshot.clock.inWorldIso;
  const conditionalAnswers = Object.values(snapshot.answers)
    .filter((answer) => answer.status === 'confirmed' && answer.value != null)
    .map((answer) => `- ${answer.questionId}: ${answerValue(answer.value)}`);
  const connected = snapshot.connected;
  const rhythm = connected.rhythm.skipped
    ? 'skipped'
    : `${connected.rhythm.type}${connected.rhythm.otherDescription ? ` (${connected.rhythm.otherDescription})` : ''}`;
  const timePeriod = [
    snapshot.timePeriod.present,
    snapshot.timePeriod.period,
    snapshot.timePeriod.rationale ? `why: ${snapshot.timePeriod.rationale}` : '',
  ].filter(Boolean).join(' | ');
  return [
    'WORLD CONTEXT (confirmed Create World handoff):',
    `Location name: ${snapshot.location.name}`,
    `Location region: ${snapshot.location.region}`,
    `Location language: ${snapshot.location.language}`,
    `Location description: ${connected.description}`,
    `Place type: ${connected.locationType}`,
    ...(timePeriod ? [`Time period: ${timePeriod}`] : []),
    `Operating days: ${schedule.daysOpen.join(', ')}`,
    `Operating hours: ${schedule.hasOperatingHours ? `${schedule.openTime}-${schedule.closeTime}` : '24/7'}`,
    `Operating timezone: ${schedule.timezone ?? schedule.timezoneStrategy}`,
    `World clock: ${formatWorldClockPrompt(clockIso, schedule, snapshot.location.region)}. Use this year, date, and clock. Do not invent a different year or timezone.`,
    `Closed behavior: ${schedule.closedBehavior}`,
    `Connected crowd: ${connected.crowd.type}${connected.crowd.otherDescription ? ` (${connected.crowd.otherDescription})` : ''}`,
    `Connected rhythm: ${rhythm}`,
    'Confirmed conditional answers:',
    ...(conditionalAnswers.length > 0 ? conditionalAnswers : ['- (none)']),
    speakerWorldBlock(a, handoff),
    speakerWorldBlock(b, handoff),
  ].join('\n');
}

// Sleep context for dialogue: a woken sleeper must read groggy, not fresh.
// Empty for awake characters (keeps the prompt lean in the common case).
export function sleepBlock(name: string, sleep: SleepState): string {
  if (sleep === 'napping') {
    return `${name} is NAPPING — drowsy, heavy-lidded, was just woken up for this chat. Write them slow, yawning, half-with-it, losing the thread.`;
  }
  if (sleep === 'deep') {
    return `${name} was in DEEP SLEEP and got dragged awake (only a direct push does that). Groggy, disoriented, possibly grumpy about being woken.`;
  }
  return '';
}

export function buildDialoguePrompt(
  a: Npc, b: Npc,
  aRel: Relationship | null, bRel: Relationship | null,
  aMems: Memory[], bMems: Memory[],
  aLong: string, bLong: string,
  cls: LengthClass,
  aSleep: SleepState = 'awake',
  bSleep: SleepState = 'awake',
  scenario: Scenario = SCENARIOS[0],
  handoff: WorldLaunchHandoff | null = null,
  bannedOpenings: readonly string[] = [],
  worldNowIso?: string,
): string {
  const [lo, hi] = dialogueTargetCount(cls);
  const aName = `${a.firstName} ${a.lastName}`;
  const bName = `${b.firstName} ${b.lastName}`;
  const aSl = sleepBlock(aName, aSleep);
  const bSl = sleepBlock(bName, bSleep);
  const sleepNote = aSl || bSl ? `\nSLEEP STATE (write drowsy characters drowsy):\n${[aSl, bSl].filter(Boolean).join('\n')}\n` : '';
  // Strangers vs familiar: no memories (recent or condensed) either way means
  // this is a first meeting. Wizard bond kinds override stranger framing.
  const hasBondKind = (rel: Relationship | null): boolean =>
    Boolean(rel?.bondKind && rel.bondKind !== 'stranger');
  const strangers =
    aMems.length === 0 && bMems.length === 0 && !aLong.trim() && !bLong.trim()
    && !hasBondKind(aRel) && !hasBondKind(bRel);
  const banned = bannedOpenings.length > 0
    ? bannedOpenings.map((line) => `- ${line}`).join('\n')
    : '- (none)';
  const openingRule = [
    'OPENING RULE: For the first parsed line only, do not open by commenting on the time of day, how early or late it is, or someone being awake or up.',
    'The clock may be mentioned later in the chat, but not as the first line.',
    'The first line is one character speaking a private thought.',
    'The thought may be about anything, from anywhere in their life.',
    'It is not about the room, the clock, or what they were just doing.',
    'Each conversation starts from a different thought. Do not continue the previous topic.',
    'Do not reuse or closely paraphrase any banned opening below.',
    `Banned openings:\n${banned}`,
  ].join(' ');
  const historyNote = strangers
    ? 'These two are STRANGERS meeting for the very first time — no shared history. First impressions, natural introductions, feeling each other out.'
    : hasBondKind(aRel) || hasBondKind(bRel)
      ? 'These two already have the relationship described above — talk accordingly (a sister is not introduced as a stranger). Familiarity may still shift after this chat.'
      : 'These two HAVE met before and carry shared history (see memories above). No introductions needed — pick up like people who know each other.';
  return `Write a ${cls} dialogue (${lo}-${hi} exchanges) between these two characters.
SCENE: ${scenario.scene}
Treat the scene as backdrop, not plot: don't make every exchange about the setting. Personalities, history, and mood lead; the setting just colors details.
HISTORY: ${historyNote}
TOPIC: pick something ordinary and varied — ${scenario.topicLean}. Most chats are mundane small talk; some turn deep; arguments happen but are the exception. Avoid cliche setups (no disputes over seats, chairs, furniture, or who was here first).
TONE: raw and honest — awkwardness and uncomfortable topics are fine, no forced politeness. Keep it edgy-but-safe: NO explicit sexual or graphic violent content.
You may draw on their memories of each other but it is not always required.
FORMAT (exact, nothing else): each line must be "Full Name: what they say" then a newline. Use full names exactly "${aName}" and "${bName}". No narration, no stage directions, no extra blank commentary.
${sleepNote}
${profileBlock(a)}
${relBlock(aRel, aMems, aLong, aName, bName)}

${profileBlock(b)}
${relBlock(bRel, bMems, bLong, bName, aName)}
${handoff ? `\n${worldBlock(handoff, a, b, worldNowIso)}` : ''}
${openingRule}`;
}

export function buildDeltaPrompt(aName: string, bName: string, transcript: string): string {
  return `Given this conversation transcript, how does it affect each character's feelings toward the other? Reply with ONLY JSON:
{"a_to_b": {"familiarity": 0-2, "affinity": -2-2, "trust": -2-2, "respect": -2-2, "fear": -2-2, "attraction": -2-2},
 "b_to_a": {...same...},
 "memories": [{"from": "a"|"b", "summary": one short concrete line about what happened (no analysis), "salience": 1-10}]}
Scale: 0 = low impact, ±1 = moderate, ±2 = strong. familiarity never decreases. 1-2 memories per side max.
${aName} = a, ${bName} = b.
Transcript:\n${transcript}`;
}

export function buildCondensePrompt(existing: string, bumped: Memory[]): string {
  const lines = bumped.map((m) => `[${m.salience}] ${m.summary}`).join('\n');
  return `Condense these older relationship memories into 2-4 sentences of long-term impression, appending to (not replacing) the existing summary. Reply with ONLY the new full summary text (max 120 words).\nExisting: ${existing || '(none)'}\nFolding in:\n${lines}`;
}
